import React from 'react'
import { useToggle } from 'react-use'
import { BusyButtonLink } from '@ztechapps/webui.button'
import type { BusyButtonProps } from '@ztechapps/webui.button'
import { useConfirmDialog, useError, useToast } from '@ztechapps/common.hook'
import type { OrderStatusEnum } from '@ztechapps/common.contract'
import { OrderPaymentStatusEnum } from '@ztechapps/common.contract'
import { COMMITTED_ORDER_STATUSES } from '@ztechapps/order.common'
import { useMarkOrderNotReceivable } from './useMarkOrderNotReceivable'
import { OrderMarkNotReceivableModal } from './OrderMarkNotReceivableModal'

export type OrderMarkNotReceivableButtonProps = {
  orderId: string
  paymentStatus?: OrderPaymentStatusEnum
  status?: OrderStatusEnum
  // Refresh the affected order/list after the status changes.
  onSuccess?: () => void | Promise<void>
  disabled?: boolean
  size?: BusyButtonProps['bsSize']
  className?: string
}

/**
 * Single entry point for the Not-Receivable workflow, reusable across the order
 * detail action area and the company accounting statement rows.
 *
 * - Order NOT yet Not Receivable → "Uncollectible" opens the reason modal
 *   (reason required) and marks via `value: true`.
 * - Order already Not Receivable → "Receivable" confirms and reverts via
 *   `value: false` (no reason; the server re-derives the real payment status).
 */
export const OrderMarkNotReceivableButton = ({
  orderId,
  paymentStatus,
  status,
  onSuccess,
  disabled = false,
  size,
  className,
}: OrderMarkNotReceivableButtonProps) => {
  const [showModal, toggleModal] = useToggle(false)
  const [markOrderReceivable, { loading, error }] = useMarkOrderNotReceivable()
  const { showSuccess } = useToast()
  useError(error)

  const isNotReceivable = paymentStatus === OrderPaymentStatusEnum.notReceivable

  const { onConfirm: confirmMarkReceivable } = useConfirmDialog({
    title: 'Mark this order as Receivable?',
    text: 'This re-derives the real payment status and includes the order in accounts receivable again.',
    buttons: { confirm: { text: 'Yes, mark as receivable' } },
    confirmed: async () => {
      await markOrderReceivable({ orderId, value: false })
      showSuccess({ message: 'Order marked as Receivable.' })
      await onSuccess?.()
    },
  })

  // Receivable only applies to committed orders — a quotation / in-cart / cancelled
  // order has nothing to collect, so the action is irrelevant and the button hides.
  if (!status || !COMMITTED_ORDER_STATUSES.includes(status)) {
    return null
  }

  // Nothing to collect on a fully/over-paid order — the money is already in, so hide
  // "Mark As Not Receivable". The reverse "Mark As Receivable" below still shows for
  // already-marked orders, which are never in a paid status.
  if (
    !isNotReceivable &&
    (paymentStatus === OrderPaymentStatusEnum.paid || paymentStatus === OrderPaymentStatusEnum.overPaid)
  ) {
    return null
  }

  if (isNotReceivable) {
    return (
      <BusyButtonLink
        bsSize={size}
        className={className}
        icon="rotate-left"
        text="Receivable"
        title="Revert this order to its real payment status"
        disabled={disabled}
        isBusy={loading}
        onClick={confirmMarkReceivable}
      />
    )
  }

  return (
    <>
      <BusyButtonLink
        bsSize={size}
        className={className}
        icon="sack-xmark"
        text="Not Receivable"
        title="Mark this order uncollectible and exclude it from accounts receivable"
        disabled={disabled}
        onClick={toggleModal}
      />
      <OrderMarkNotReceivableModal orderId={orderId} show={showModal} onHide={toggleModal} onSuccess={onSuccess} />
    </>
  )
}
